<template>
  <div class="min-h-screen flex items-center justify-center p-4 auth-background">
    <div class="relative max-w-md w-full">
      <div class="glassmorphic-card">
        <div class="text-center mb-8">
          <router-link to="/" class="inline-block mb-4">
            <img 
              src="/images/venmas-logo-1.png" 
              alt="Venmas" 
              class="h-16 mx-auto object-contain"
            />
          </router-link>
          <h2 class="text-3xl font-bold text-white mb-2">
            {{ isTrialPlan ? 'Start Your Free Trial' : 'Create Your Account' }}
          </h2>
          <p class="text-white/70">
            {{ isBusinessSignup ? 'Set up your business in 2 minutes' : 'Create your account' }}
          </p>
          <div v-if="isBusinessSignup && selectedPlan" class="mt-3 inline-flex items-center gap-2 bg-white/10 text-white/90 border border-white/20 px-4 py-2 rounded-full text-sm font-medium">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            {{ isTrialPlan ? '14-day free trial · No credit card required' : planDisplayName + ' plan · Payment required after signup' }}
          </div>
        </div>

        <form @submit.prevent="handleRegister" class="space-y-5">
          <!-- Business Name (shown first for business signups) -->
          <div v-if="isBusinessSignup">
            <label for="business_name" class="block text-sm font-semibold text-white/90 mb-2">Business Name</label>
            <input
              id="business_name"
              v-model="form.business_name"
              type="text"
              required
              class="glass-input"
              :class="{ 'border-red-400/50': fieldErrors.business_name }"
              placeholder="e.g. Mama Njeri's Shop"
              @blur="validateBusinessName"
            />
            <p v-if="fieldErrors.business_name" class="text-red-300 text-xs mt-1">{{ fieldErrors.business_name }}</p>
          </div>

          <!-- Phone Field -->
          <div>
            <label for="phone" class="block text-sm font-semibold text-white/90 mb-2">Phone Number</label>
            <div class="relative flex items-center glass-input !p-0 overflow-hidden" :class="{ 'border-red-400/50': fieldErrors.phone }">
              <select
                v-model="selectedCountry"
                class="h-full bg-transparent border-none outline-none text-white text-sm pl-3 pr-1 py-3 cursor-pointer appearance-none"
                style="width: auto; min-width: 72px; max-width: 90px;"
                @change="handlePhoneInput"
              >
                <option v-for="c in countries" :key="c.code" :value="c" class="bg-gray-800 text-white">
                  {{ c.flag }} {{ c.dialCode }}
                </option>
              </select>
              <div class="w-px h-6 bg-white/25 flex-shrink-0"></div>
              <input
                id="phone"
                v-model="phoneDigits"
                type="tel"
                required
                class="flex-1 bg-transparent border-none outline-none text-white text-sm placeholder-white/50 px-3 py-3 min-w-0"
                placeholder="712345678"
                :maxlength="selectedCountry.maxLength"
                @input="handlePhoneInput"
                @blur="validatePhone"
              />
            </div>
            <p v-if="fieldErrors.phone" class="text-red-300 text-xs mt-1">{{ fieldErrors.phone }}</p>
          </div>

          <!-- Email Field -->
          <div>
            <label for="email" class="block text-sm font-semibold text-white/90 mb-2">Email Address</label>
            <input
              id="email"
              v-model="form.email"
              type="email"
              required
              class="glass-input"
              :class="{ 'border-red-400/50': fieldErrors.email }"
              placeholder="you@example.com"
              @blur="validateEmail"
            />
            <p v-if="fieldErrors.email" class="text-red-300 text-xs mt-1">{{ fieldErrors.email }}</p>
          </div>

          <!-- Business Name (for customer signups, shown after email) -->
          <div v-if="!isBusinessSignup">
            <label for="business_name_customer" class="block text-sm font-semibold text-white/90 mb-2">Your Name</label>
            <input
              id="business_name_customer"
              v-model="form.business_name"
              type="text"
              required
              class="glass-input"
              :class="{ 'border-red-400/50': fieldErrors.business_name }"
              placeholder="Your full name"
              @blur="validateBusinessName"
            />
            <p v-if="fieldErrors.business_name" class="text-red-300 text-xs mt-1">{{ fieldErrors.business_name }}</p>
          </div>

          <!-- Password -->
          <div>
            <label for="password" class="block text-sm font-semibold text-white/90 mb-2">Password</label>
            <div class="relative">
              <input
                id="password"
                v-model="form.password"
                :type="showPassword ? 'text' : 'password'"
                required
                class="glass-input pr-12"
                :class="{ 'border-red-400/50': fieldErrors.password }"
                placeholder="Create a password"
                @input="validatePassword"
                @blur="validatePassword"
              />
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute inset-y-0 right-0 pr-3 flex items-center text-white/60 hover:text-white/80"
              >
                <svg v-if="!showPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                </svg>
                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"></path>
                </svg>
              </button>
            </div>
            <div class="mt-2">
              <div class="flex gap-1 mb-1">
                <div class="h-1 flex-1 rounded-full transition-all" :class="passwordStrengthColor(0)"></div>
                <div class="h-1 flex-1 rounded-full transition-all" :class="passwordStrengthColor(1)"></div>
                <div class="h-1 flex-1 rounded-full transition-all" :class="passwordStrengthColor(2)"></div>
                <div class="h-1 flex-1 rounded-full transition-all" :class="passwordStrengthColor(3)"></div>
              </div>
              <p class="text-xs text-white/60">
                {{ passwordStrengthLabel }}
              </p>
            </div>
            <p v-if="fieldErrors.password" class="text-red-300 text-xs mt-1">{{ fieldErrors.password }}</p>
          </div>

          <!-- Success/Error Messages -->
          <div v-if="error" class="p-4 bg-red-500/20 border border-red-400/30 rounded-lg text-red-200 text-sm">
            {{ error }}
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="authStore.loading || !isFormValid"
            class="w-full py-3 px-4 rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed text-white"
            style="background: #2563eb;"
          >
            <span v-if="authStore.loading" class="flex items-center justify-center">
              <div class="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              {{ isBusinessSignup ? 'Setting up your business...' : 'Creating account...' }}
            </span>
            <span v-else>
              {{ isTrialPlan ? 'Start Free Trial →' : isBusinessSignup ? 'Create Account →' : 'Create Account' }}
            </span>
          </button>

          <!-- Sign In Link -->
          <div class="text-center pt-4 border-t border-white/20">
            <p class="text-white/70 text-sm mb-3">Already have an account?</p>
            <router-link 
              to="/login" 
              class="inline-block w-full bg-white/10 text-white/80 hover:text-white hover:bg-white/15 py-2 px-4 rounded-lg font-medium transition-all border border-white/20"
            >
              Sign In
            </router-link>
          </div>

          <!-- Customer info -->
          <div class="text-center">
            <p class="text-sm text-white/60">
              Customers don't need to register — just shop and checkout with your phone number.
            </p>
          </div>
        </form>
      </div>

      <div class="mt-8 text-center">
        <p class="text-sm text-white/60">
          © 2026 Venmas · Powering Kenyan Retail
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import type { RegisterData } from '@/stores/auth'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

// Determine signup type from query param
const selectedPlan = computed(() => route.query.plan as string | undefined)
const isBusinessSignup = computed(() => !!selectedPlan.value)
const isTrialPlan = computed(() => selectedPlan.value === 'anza')
const planDisplayName = computed(() => {
  const names: Record<string, string> = { anza: 'Anza', kua_basic: 'Kua Basic', kua_pro: 'Kua Pro' }
  return names[selectedPlan.value || ''] || selectedPlan.value || ''
})

const form = reactive<RegisterData>({
  email: '',
  phone: '',
  password: '',
  business_name: '',
  role: 'admin_vendor',
  plan: selectedPlan.value || 'anza'
})

// Update plan when query param changes
watch(selectedPlan, (plan) => {
  form.plan = plan || 'anza'
  form.role = 'admin_vendor'
}, { immediate: true })

const countries = [
  { code: 'KE', dialCode: '+254', flag: '🇰🇪', maxLength: 9 },
  { code: 'UG', dialCode: '+256', flag: '🇺🇬', maxLength: 9 },
  { code: 'TZ', dialCode: '+255', flag: '🇹🇿', maxLength: 9 },
  { code: 'RW', dialCode: '+250', flag: '🇷🇼', maxLength: 9 },
  { code: 'ET', dialCode: '+251', flag: '🇪🇹', maxLength: 9 },
  { code: 'ZA', dialCode: '+27', flag: '🇿🇦', maxLength: 9 },
  { code: 'NG', dialCode: '+234', flag: '🇳🇬', maxLength: 10 },
]
const defaultCountry = countries[0] ?? { code: 'KE', dialCode: '+254', flag: '🇰🇪', maxLength: 9 }
const selectedCountry = ref(defaultCountry)
const phoneDigits = ref('')
const error = ref('')
const showPassword = ref(false)

const fieldErrors = reactive<{[key: string]: string}>({})

// Password strength (simplified — no special char requirement)
const passwordStrength = computed(() => {
  let score = 0
  if (form.password.length >= 6) score++
  if (form.password.length >= 8) score++
  if (/[A-Z]/.test(form.password) && /[a-z]/.test(form.password)) score++
  if (/[0-9]/.test(form.password) || /[^A-Za-z0-9]/.test(form.password)) score++
  return score
})

const passwordStrengthLabel = computed(() => {
  const labels = ['', 'Weak', 'Fair', 'Good', 'Strong']
  return labels[passwordStrength.value] || ''
})

function passwordStrengthColor(index: number) {
  if (index >= passwordStrength.value) return 'bg-gray-200'
  if (passwordStrength.value <= 1) return 'bg-red-400'
  if (passwordStrength.value === 2) return 'bg-yellow-400'
  if (passwordStrength.value === 3) return 'bg-blue-400'
  return 'bg-green-400'
}

const isFormValid = computed(() => {
  return form.email && 
         form.phone && 
         form.business_name && 
         form.password.length >= 6 &&
         passwordStrength.value >= 2 &&
         Object.keys(fieldErrors).length === 0
})

function handlePhoneInput() {
  phoneDigits.value = phoneDigits.value.replace(/\D/g, '').slice(0, selectedCountry.value.maxLength)
  form.phone = selectedCountry.value.dialCode + phoneDigits.value
}

function validateEmail() {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!form.email) {
    fieldErrors.email = 'Email is required'
  } else if (!emailRegex.test(form.email)) {
    fieldErrors.email = 'Please enter a valid email address'
  } else {
    delete fieldErrors.email
  }
}

function validatePhone() {
  if (!phoneDigits.value) {
    fieldErrors.phone = 'Phone number is required'
  } else if (phoneDigits.value.length !== selectedCountry.value.maxLength) {
    fieldErrors.phone = `Phone number must be ${selectedCountry.value.maxLength} digits after ${selectedCountry.value.dialCode}`
  } else {
    delete fieldErrors.phone
  }
}

function validateBusinessName() {
  if (!form.business_name) {
    fieldErrors.business_name = isBusinessSignup.value ? 'Business name is required' : 'Name is required'
  } else if (form.business_name.length < 2) {
    fieldErrors.business_name = 'Must be at least 2 characters'
  } else {
    delete fieldErrors.business_name
  }
}

function validatePassword() {
  if (!form.password) {
    fieldErrors.password = 'Password is required'
  } else if (form.password.length < 6) {
    fieldErrors.password = 'Password must be at least 6 characters'
  } else if (passwordStrength.value < 2) {
    fieldErrors.password = 'Password is too weak'
  } else {
    delete fieldErrors.password
  }
}

async function handleRegister() {
  error.value = ''
  
  validateEmail()
  validatePhone()
  validateBusinessName()
  validatePassword()

  if (!isFormValid.value) {
    error.value = 'Please fix the errors above'
    return
  }

  try {
    await authStore.register(form)
    await router.replace('/dashboard')
  } catch (err: unknown) {
    const apiError = (err as { error?: unknown })?.error
    error.value =
      typeof apiError === 'string'
        ? apiError
        : 'Registration failed. Please check your information.'
  }
}
</script>

<style scoped>
.auth-background {
  background: #1e3a5f url('/images/auth-bg.png') center / cover no-repeat fixed;
  position: relative;
  isolation: isolate;
}

.auth-background::before {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  z-index: 0;
}

.auth-background > * {
  position: relative;
  z-index: 1;
}

.glassmorphic-card {
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
  padding: 2rem;
  transition: all 0.3s ease;
}

.glassmorphic-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
}

.glass-input {
  width: 100%;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: white;
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 14px;
  transition: all 0.2s ease;
}

.glass-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.glass-input:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.5);
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.2);
}

button[type="submit"]:hover {
  background: #1d4ed8 !important;
}

/* Password strength indicators */
.bg-gray-200 { background-color: rgba(255, 255, 255, 0.2); }
.bg-red-400 { background-color: #f87171; }
.bg-yellow-400 { background-color: #fbbf24; }
.bg-blue-400 { background-color: #60a5fa; }
.bg-green-400 { background-color: #4ade80; }
</style>
